import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SecureStorageService } from '../../services/secure-storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard  {
  private permissions: any = null;
  private role: any = null;

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly secureStorage: SecureStorageService
  ){ }
  
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {      
      
    return this.checkAuth(state);
  }

  private async checkAuth(state: RouterStateSnapshot): Promise<boolean> {
    let allowed: boolean = false;
    
    try {
      const currentAuth = await this.secureStorage.getItem('token');
      const permissionsStr = await this.secureStorage.getItem('permissions');
      const roleObj = await this.secureStorage.getObject('role');
      
      this.permissions = permissionsStr ? JSON.parse(permissionsStr) : null;
      this.role = roleObj;

      // Check if user is authenticated
      if (currentAuth == null || this.permissions == null || this.role == null) {
        this.router.navigate(['sign-in']);
        return false;
      }
      
      return this.validatePermissions(state);
    } catch (error) {
      console.error('Error checking authentication:', error);
      // Fallback to localStorage for backward compatibility
      return this.checkAuthFallback(state);
    }
  }

  private checkAuthFallback(state: RouterStateSnapshot): boolean {
    let allowed: boolean = false;
    let currentAuth = localStorage.getItem('token');
    this.permissions = localStorage.getItem('permissions') ? JSON.parse(localStorage.getItem('permissions')!) : null;
    this.role = localStorage.getItem('role') ? JSON.parse(localStorage.getItem('role')!) : null;

    // Check if user is authenticated
    if (currentAuth == null || this.permissions == null || this.role == null) {
      this.router.navigate(['sign-in']);
      return false;
    }

    return this.validatePermissions(state);
  }

  private validatePermissions(state: RouterStateSnapshot): boolean {
    let allowed: boolean = false;

    try {
      let url = state.url.split('/').filter(Boolean);
      
      // Handle root path or empty URL
      if (url.length === 0) {
        return true; // Allow access to root/dashboard
      }

      let dic = this.dict(url[0]);
      let seg: any = this.serializer(url[0]);
      
      // If we can't determine the module or segment, deny access
      if (!dic || !seg) {
        console.warn(`Unknown route attempted: ${state.url}`);
        this.router.navigate(['dashboard']);
        return false;
      }

      // Check if the module exists in permissions
      if (!this.permissions.modules[dic]?.[seg]) {
        // User doesn't have permission for this route
        this.router.navigate(['dashboard']);
        return false;
      }

      // Check if user has permission for this route
      allowed = this.permissions.modules[dic][seg].active;
      
      if (!allowed) {
        console.warn(`Access denied to ${state.url} for user role ${this.role.role.id}`);
        this.router.navigate(['dashboard']);
        return false;
      }

    } catch (error) {
      console.error('Error checking permissions:', error);
      this.router.navigate(['sign-in']);
      return false;
    }

    return allowed;
  }

  private dict(item: any) {
    let group = null;
    if (item == 'dashboard' || item == 'schools' || item == 'comms' || item == 'calendar' || item == 'notices' || item == 'notifications' || item == 'students' || item == 'profile' || item == 'roles' || item == 'tutors' || item == 'settings' || item == 'families' || item == 'professors' || item == 'polls') {
      group = 'admin';
    } else if (item == 'technicians' || item == 'users' || item == 'tenants' || item == 'perfiles' || item == 'roles') {
      group = 'config';
    } else if (item == 'maintenances' || item == 'amenidades' || item == 'providers' || item == 'services' || item == 'combustibles') {
      group = 'operation';
    } else if (item == 'tenant') {
      group = 'tenants';
    }
    return group;
  }

  private serializer(item: any) {
    let segment = null;
    if (item == 'dashboard') {
      segment = 'dashboard';
    }
    if (item == 'schools') {
      segment = 'schools';
    }
    if (item == 'comms') {
      segment = 'comms';
    }
    if (item == 'calendar') {
      segment = 'calendar';
    }
    if (item == 'notices') {
      segment = 'notices';
    }
    if (item == 'notifications') {
      segment = 'notifications';
    }
    if (item == 'users') {
      segment = 'profile';
    }
    if (item == 'roles') {
      segment = 'roles';
    }
    if (item == 'tutors') {
      segment = 'tutors';
    }
    if (item == 'students') {
      segment = 'students';
    }
    if (item == 'settings') {
      segment = 'settings';
    }
    if (item == 'families') {
      segment = 'families';
    }
    if (item == 'professors') {
      segment = 'professors';
    }
    if (item == 'polls') {
      segment = 'polls';
    }
    
    return segment;
  }
  
}
